/**
 * Tiny "…" overflow menu — a trigger button that opens a dropdown of items.
 *
 * Closes when an item is clicked, on outside click, on Esc. We intentionally
 * do NOT use `onMouseLeave` on the wrapper: the dropdown panel is a child of
 * the wrapper but rendered `absolute` with a small `mt-1` gap, so leaving the
 * trigger briefly while crossing that gap would close the menu before the user
 * can reach the items.
 *
 * For accessibility, the trigger has `aria-haspopup="menu"` and the panel has
 * `role="menu"`.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface KebabMenuItem {
  key: string
  label: ReactNode
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
}

interface KebabMenuProps {
  items: KebabMenuItem[]
  /** Optional label for screen readers on the trigger button. */
  ariaLabel?: string
  className?: string
}

export function KebabMenu({ items, ariaLabel = 'More actions', className }: KebabMenuProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((o) => !o)
        }}
        className="rounded-md p-1 text-surface-500 hover:bg-surface-100 hover:text-surface-700 dark:hover:bg-surface-800 dark:hover:text-surface-200"
      >
        {/* Three dots — keep simple so it matches the rest of the app. */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="3" cy="8" r="1.4" fill="currentColor" />
          <circle cx="8" cy="8" r="1.4" fill="currentColor" />
          <circle cx="13" cy="8" r="1.4" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 py-1 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation()
                if (item.disabled) return
                setOpen(false)
                item.onSelect()
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                'hover:bg-surface-100 dark:hover:bg-surface-800',
                'disabled:cursor-not-allowed disabled:opacity-50',
                item.destructive ? 'text-red-600 dark:text-red-300' : 'text-surface-700 dark:text-surface-200',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
